import React, { useMemo, useState } from 'react'
import Dialog from '@components/ui/DialogNew/Dialog'
import { RENTAL_OPTIONS, NO_PAYMENT_OPTIONS_AVAILABLE } from '@constants'
import { useBreakpoint } from '@shared/hooks/useBreakpoint'
import { IBookRentalOptionRowHeaders, IBookRentalOptions } from '@features/book/types'
import { CompareOptions } from './CompareOptions'
import { formatOptionValueOutput, getSelectValueFromEvent } from './MkRentalOtpionsModalUtils'
import './MkRentalOptionsModal.scss'

const NoPaymentOptionsAvailable: React.FC<{ visible: boolean; onHide: () => void }> = ({ visible, onHide }) => (
  <Dialog
    className="mk-rental-options-modal"
    title={RENTAL_OPTIONS}
    size="large"
    visible={visible}
    onClose={onHide}>
    <div>
      <p>{NO_PAYMENT_OPTIONS_AVAILABLE}</p>
    </div>
  </Dialog>
)

export interface IMkRentalOptionsModalProps {
  onHide: () => void
  visible: boolean
  paymentOptions: IBookRentalOptions
}

export const MkRentalOptionsModal: React.FC<IMkRentalOptionsModalProps> = ({ onHide, visible, paymentOptions }) => {
  const { isMobile } = useBreakpoint()
  const { rowHeaders, columns } = paymentOptions
  const { overview, ...restRowHeaders } = rowHeaders
  const rearrangedRowHeaders = { overview, ...restRowHeaders }

  const [compareLeftKey, setCompareLeftKey] = useState('')
  const [compareRightKey, setCompareRightKey] = useState('')

  const showCompareOptions = columns.length >= 2

  const effectiveCompareLeft = showCompareOptions ? compareLeftKey || (columns[0]?.key ?? '') : ''
  const effectiveCompareRight = showCompareOptions ? compareRightKey || (columns[1]?.key ?? '') : ''

  const columnsToShow = useMemo(() => {
    if (!showCompareOptions || !effectiveCompareLeft || !effectiveCompareRight) return columns
    const left = columns.find((c) => c.key === effectiveCompareLeft)
    const right = columns.find((c) => c.key === effectiveCompareRight)
    if (!left || !right) return columns
    return [left, right]
  }, [columns, showCompareOptions, effectiveCompareLeft, effectiveCompareRight])

  const leftOptions = useMemo(() => columns.filter((c) => c.key !== effectiveCompareRight).map((c) => ({ value: c.key, label: c.name })), [columns, effectiveCompareRight])
  const rightOptions = useMemo(() => columns.filter((c) => c.key !== effectiveCompareLeft).map((c) => ({ value: c.key, label: c.name })), [columns, effectiveCompareLeft])

  // Safety check to ensure paymentOptions is a valid array
  if (!rowHeaders || !columns || columns.length === 0) {
    return (
      <NoPaymentOptionsAvailable
        visible={visible}
        onHide={onHide}
      />
    )
  }

  const tableHeaders = columns.map((column) => <th key={column.key}>{column.name}</th>)
  const tableRows = Object.entries(rearrangedRowHeaders).map(([headerKey, headerValue]) => (
    <tr
      key={headerKey}
      className="mk-rental-options-modal__table-row">
      <th>{headerValue}</th>
      {columns.map((column) => (
        <td key={column.key}>{formatOptionValueOutput(column[headerKey as keyof IBookRentalOptionRowHeaders] as boolean | string | string[])}</td>
      ))}
    </tr>
  ))

  const compareRows = Object.entries(rearrangedRowHeaders).map(([headerKey, headerValue]) => (
    <div
      key={headerKey}
      className="mk-rental-options-modal__comparison-row">
      <div className="mk-rental-options-modal__comparison-row-label">{headerValue}</div>
      <div className="mk-rental-options-modal__comparison-row-split">
        {columnsToShow.map((column) => (
          <div key={column.key}>{formatOptionValueOutput(column[headerKey as keyof IBookRentalOptionRowHeaders] as boolean | string | string[])}</div>
        ))}
      </div>
    </div>
  ))

  return (
    <Dialog
      className="mk-rental-options-modal"
      title={RENTAL_OPTIONS}
      size="large"
      visible={visible}
      onClose={onHide}>
      <div>
        {isMobile ? (
          <>
            {showCompareOptions ? (
              <CompareOptions
                leftOptions={leftOptions}
                rightOptions={rightOptions}
                selectedLeftKey={effectiveCompareLeft}
                selectedRightKey={effectiveCompareRight}
                onLeftChange={(e) => setCompareLeftKey(getSelectValueFromEvent(e))}
                onRightChange={(e) => setCompareRightKey(getSelectValueFromEvent(e))}
              />
            ) : (
              <div className="mk-rental-options-modal__single-column-label">{columns[0].name}</div>
            )}
            <div className="mk-rental-options-modal__compare-body">{compareRows}</div>
          </>
        ) : (
          <table className="mk-rental-options-modal__table">
            <thead>
              <tr>
                <th aria-label="Option Name" />
                {tableHeaders}
              </tr>
            </thead>
            <tbody>{tableRows}</tbody>
          </table>
        )}
      </div>
    </Dialog>
  )
}
